import React, { useEffect } from "react";
import { Box, Grid, InputAdornment } from "@material-ui/core";
import { SelectSeason } from "ui/componentes/SelectSeason";
import { InputFieldFormik } from "ui/componentes/Form/InputFieldFormik";
import {
    SelectFieldFormik,
    SelectOption,
} from "ui/componentes/Form/SelectFieldFormik";
import { SwitchFieldFormik } from "ui/componentes/Form/SwitchFieldFormik";
import {
    Championship,
    ChampionshipType,
    ChampionshipTypeDescription,
} from "api/model/Championship";
import { useFormikContext } from "formik";
import { FileVisualizer } from "ui/componentes/FileVisualizer";
import { BucketFolders } from "api/constants";
import { useCurrentSeason } from "api";

const championshipTypeOptions: SelectOption[] = [
    {
        label: ChampionshipTypeDescription[ChampionshipType.OVAL],
        value: ChampionshipType.OVAL,
    },
    {
        label: ChampionshipTypeDescription[ChampionshipType.ROAD],
        value: ChampionshipType.ROAD,
    },
];

const ChampionshipDetails = () => {
    const {
        values,
        submitForm,
        setFieldValue,
    } = useFormikContext<Championship>();
    const currentSeason = useCurrentSeason() || {};

    useEffect(() => {
        if (!values.seasonId) {
            setFieldValue("seasonId", currentSeason.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSeason.id, values.seasonId]);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Box display="flex">
                    <SelectSeason />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <InputFieldFormik id="name" name="name" label="Nome" />
            </Grid>
            <Grid item xs={12}>
                <InputFieldFormik
                    id="description"
                    name="description"
                    label="Descrição"
                    multiline
                    rows={10}
                    helperText={`Max.: ${values.description?.length} / 600`}
                />
            </Grid>
            <Grid item xs={12}>
                <SelectFieldFormik
                    id="championshipType"
                    name="championshipType"
                    label="Tipo de campeonato"
                    options={championshipTypeOptions}
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="discards"
                    name="discards"
                    label="Descartes"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="amountDriversByTeam"
                    name="amountDriversByTeam"
                    label="Quantos pilotos por equipe?"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="driversToScore"
                    name="driversToScore"
                    label="Quantos pilotos pontuarão para equipe por corrida?"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="completedLapsToScore"
                    name="completedLapsToScore"
                    label="Percentual de voltas para marcar pontos"
                    type="number"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                        ),
                    }}
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="fastLapBonusPoint"
                    name="fastLapBonusPoint"
                    label="Ponto(s) bonus por volta rápida"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="poleBonusPoint"
                    name="poleBonusPoint"
                    label="Ponto(s) bonus por pole position"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="safeDriverBonusPoint"
                    name="safeDriverBonusPoint"
                    label="Ponto(s) bonus por não cometer incidentes"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="gainPositionsBonusPoint"
                    name="gainPositionsBonusPoint"
                    label="Ponto(s) bonus por ganhar mais posições"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="lapsLedBonusPoint"
                    name="lapsLedBonusPoint"
                    label="Ponto(s) bonus por liderar mais voltas"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="oneLapLedBonusPoint"
                    name="oneLapLedBonusPoint"
                    label="Ponto(s) bonus por liderar uma volta"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="infractionSettings.maxPointsToSuspend"
                    name="infractionSettings.maxPointsToSuspend"
                    label="Ponto(s) por punição para ficar suspenso da próxima etapa"
                    type="number"
                />
            </Grid>
            <Grid item md={3} xs={12}>
                <InputFieldFormik
                    id="infractionSettings.maxPointsToStartFromPitLane"
                    name="infractionSettings.maxPointsToStartFromPitLane"
                    label="Ponto(s) por punição para largar dos boxes na próxima etapa"
                    type="number"
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchFieldFormik
                    name="losesPointsChangingCar"
                    label="Perde pontos ao trocar de carro?"
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchFieldFormik
                    name="championshipByTeam"
                    label="Campeonato por equipes?"
                />
            </Grid>
            <Grid item xs={12}>
                <FileVisualizer
                    label="Imagem"
                    file={values.image}
                    uploadProps={{
                        name: "image",
                        accept: "image/*",
                        folder: BucketFolders.CHAMPIONSHIPS,
                        disabled: !values.id,
                        onComplete: () => submitForm(),
                    }}
                    onDelete={() => submitForm()}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchFieldFormik name="isFixedSetup" label="Setup fixo?" />
            </Grid>
            <Grid item xs={12}>
                <SwitchFieldFormik
                    name="public"
                    label="Visível para pilotos?"
                />
            </Grid>
        </Grid>
    );
};

export { ChampionshipDetails };
